<!-- components/ScrollSwipe.vue -->
<template>
  <van-swipe
      lazy-render
      ref="swipeRef"
      vertical
      :loop="false"
      :touchable="false"
      :style="{ height: height + 'px' ,width:'100%'}"
      :show-indicators="false"
      @change="swiperChange"
  >
    <van-swipe-item
        v-for="(pageData, pageIndex) in data"
        :key="pageIndex"
    >
      <div
          class="scroll-container"
          :style="{ height: height + 'px' }"
          :ref="el => setScrollRef(el, pageIndex)"
          @touchstart="onTouchStart($event, pageIndex)"
          @touchend="onTouchEnd($event, pageIndex)"
      >
        <slot :items="pageData" :page-index="pageIndex" />
      </div>
    </van-swipe-item>
  </van-swipe>
</template>

<script setup>
  import { ref } from 'vue'
  defineExpose({
    changeSwiperPage
  })
  const props = defineProps({
    data: {
      type: Array, // 二维数组
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    onSwipeChange: Function,
    // 切换分栏时是否把对应右侧内容重置回顶部(默认 false, 保持原有行为)
    resetTopOnChange: {
      type: Boolean,
      default: false,
    },
  })

  function swiperChange(index){
    currentPageIndex.value = index;
    if(props.onSwipeChange){
      props.onSwipeChange(index);
    }
  }

  const scrollRefs = ref([])
  const swipeRef = ref();

  const scrollPositionY = ref(0) // ✨ 新增：記錄每頁的 scrollTop
  const currentPageIndex = ref(0) // ✨ 新增：當前所在的 pageIndex

  onActivated(()=>{
    resetScrollPosition(currentPageIndex.value)
  });
  function resetScrollPosition(index) {
    nextTick(() => {
      const el = scrollRefs.value[index]
      if (el) {
        el.scrollTop = scrollPositionY.value || 0
      }
    })
  }
  function changeSwiperPage(index) {
    swipeRef.value.swipeTo(index);
    // 仅手动点击左侧大类切换时, 让该分栏右侧内容回到顶部(上滑/下滑手势翻页不触发)
    if (props.resetTopOnChange) {
      scrollPositionY.value = 0;
      nextTick(() => {
        const el = scrollRefs.value[index];
        if (el) el.scrollTop = 0;
      });
    }
  }

  function setScrollRef(el, index) {
    if (el) {
      scrollRefs.value[index] = el;

      el.onscroll = () => {
        scrollPositionY.value = el.scrollTop
      }

      // 项目全局启用了 default-passive-events, Vue 的 @touchmove 是被动监听、无法 preventDefault;
      // 这里用非被动监听手动绑定, 以便在边缘过度滑动时阻止外层整页跟随滚动/回弹。
      el.__pageIndex = index;
      if (!el.__hMoveBound) {
        el.__hMoveBound = true;
        el.addEventListener('touchmove', (e) => onTouchMove(e, el.__pageIndex), { passive: false });
      }
    }
  }

  // ── 上下滑翻页(取代 van-swipe 自身手势, 见模板 :touchable="false") ──────────────
  // 旧实现靠 stopPropagation 把手势冒泡给 van-swipe; 但快速连续上滑时, 新触摸会打断惯性、
  // 把滚动停在列表中段, "未到底"的 touchmove 全被拦掉 → van-swipe 收到的位移≈0 → 不翻页,
  // 必须等滚动停在底部、下一次手势"从底部开始"才翻页(用户反馈的卡顿来源)。
  // 现改为自己判定: 内容滚到边缘后, 手指继续朝边缘方向再拖过一个阈值 → 翻页。
  const EDGE_TOL = 2        // 判定到顶/到底的像素容差
  const SWITCH_DIST = 45    // 到边后还需继续拖动这么多 px 才翻页
  const QUICK_DIST = 12     // 快速轻扫时的最小继续拖动距离
  const QUICK_SPEED = 0.3   // 快速轻扫速度阈值(px/ms)
  let gStartY = 0, gLastY = 0, gStartTime = 0, gEdgeY = null, gEdgeDir = 0 // -1=上滑到底 / 1=下滑到顶

  function onTouchStart(e) {
    gStartY = gLastY = e.touches[0].clientY
    gStartTime = Date.now()
    gEdgeY = null
    gEdgeDir = 0
  }

  function onTouchMove(e, index) {
    const scrollEl = scrollRefs.value[index]
    if (!scrollEl) return
    const currentY = e.touches[0].clientY
    gLastY = currentY
    const deltaY = currentY - gStartY
    const { scrollTop, scrollHeight, clientHeight } = scrollEl
    const isAtTop = scrollTop <= EDGE_TOL
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - EDGE_TOL
    // 记录"首次到达边缘"那一刻的手指位置, 之后超出的部分才算翻页拖动(内部滚动由原生 overflow 处理)
    if (gEdgeY === null) {
      if (deltaY < 0 && isAtBottom) { gEdgeY = currentY; gEdgeDir = -1 }
      else if (deltaY > 0 && isAtTop) { gEdgeY = currentY; gEdgeDir = 1 }
    }
    // 已到边缘还继续朝边缘方向拖(到底上滑 / 到顶下拉): 阻止默认, 否则会把外层整个首页一起带着滚动/回弹
    if ((deltaY < 0 && isAtBottom) || (deltaY > 0 && isAtTop)) {
      if (e.cancelable) e.preventDefault()
    }
  }

  function onTouchEnd(e, index) {
    if (gEdgeY === null) return
    const endY = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientY : gLastY
    const past = gEdgeDir === -1 ? (gEdgeY - endY) : (endY - gEdgeY) // 到边后继续朝边缘方向拖的距离
    if (past <= 0) return
    const duration = Math.max(1, Date.now() - gStartTime)
    const speed = past / duration
    if (!(past >= SWITCH_DIST || (past >= QUICK_DIST && speed >= QUICK_SPEED))) return
    const last = (props.data?.length ?? 0) - 1
    if (gEdgeDir === -1 && index < last) {
      swipeRef.value?.swipeTo(index + 1)
    } else if (gEdgeDir === 1 && index > 0) {
      swipeRef.value?.swipeTo(index - 1)
    }
  }
</script>

<style scoped>
  .scroll-container {
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
  }
  .scroll-container::-webkit-scrollbar {
    display: none;
  }
</style>
